import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "@/components/base/Modal";
import { money } from "@/lib/format";
import { supabase } from "@/lib/supabase";
import { connectorAuthLabel, connectorDisplayName, connectorReadyForDrafts, type ConnectorIdentityInput } from "@/lib/connector-identity";
import { createChannelListing, type InventoryStrategy, type MediaStrategy, type PriceStrategy } from "@/lib/channels";
import { isAmazonProvider } from "@/lib/providers/amazon-au";
import { workspaceColumnClass } from "@/lib/channel-taxonomy";
import {
  buildDraftProviderConfig,
  calculatedDraftPrice,
  createSelectedChannelDrafts,
  emptyChannelDraft,
  validateChannelDraft,
  type ChannelWorkspaceDraft,
} from "@/lib/listing-workspace";
import { ChannelCategoryPicker } from "@/pages/products/detail/components/ChannelCategoryPicker";
import { effectiveSellingPrice } from "@/lib/products";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";

export type WorkspaceVariant = {
  id: string;
  sku: string | null;
  option_values: Record<string, string> | null;
  selling_price: number | null;
};

export type WorkspaceImage = {
  mediaAssetId: string;
  filename: string | null;
  isPrimary: boolean;
};

export function ListingWorkspaceModal(props: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  productId: string;
  productName: string;
  productDescription: string | null;
  productCategoryName?: string | null;
  productSellingPrice?: number | null;
  connectors: ConnectorIdentityInput[];
  variants: WorkspaceVariant[];
  images: WorkspaceImage[];
  onCreated: () => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, ChannelWorkspaceDraft>>({});
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState("");
  const [perChannelError, setPerChannelError] = useState<Record<string, string>>({});

  const listingConnectors = useMemo(
    () => props.connectors.filter((c) => !isAmazonProvider(c.provider)),
    [props.connectors],
  );
  const variantKey = props.variants.map((v) => v.id).join(",");
  const variantIds = useMemo(() => (variantKey ? variantKey.split(",") : []), [variantKey]);
  const connectorKey = listingConnectors.map((c) => `${c.id}:${c.authStatus ?? ""}`).join(",");
  const basePrice = Number(effectiveSellingPrice(props.variants[0]?.selling_price, props.productSellingPrice) ?? 0);
  const hints = [props.productCategoryName ?? "", props.productName].filter(Boolean);

  const connectorsRef = useRef(listingConnectors);
  connectorsRef.current = listingConnectors;

  useEffect(() => {
    if (!props.open) return;
    setBanner("");
    setPerChannelError({});
  }, [props.open]);

  useEffect(() => {
    if (!props.open) return;
    const list = connectorsRef.current;
    setDrafts((prev) => {
      const next: Record<string, ChannelWorkspaceDraft> = {};
      for (const c of list) {
        next[c.id] = prev[c.id] ?? {
          ...emptyChannelDraft(c.id, props.productName, props.productDescription ?? "", variantIds),
          selected: connectorReadyForDrafts(c.authStatus),
        };
      }
      return next;
    });
  }, [props.open, connectorKey, props.productName, props.productDescription, variantIds]);

  const selectedCount = listingConnectors.filter((c) => drafts[c.id]?.selected).length;

  const patch = (connectorId: string, partial: Partial<ChannelWorkspaceDraft>) => {
    setDrafts((cur) => {
      const existing = cur[connectorId] ?? emptyChannelDraft(connectorId, props.productName, props.productDescription ?? "", variantIds);
      return { ...cur, [connectorId]: { ...existing, ...partial } };
    });
  };

  const toggleAll = (selected: boolean) => {
    setDrafts((cur) => {
      const next = { ...cur };
      for (const c of listingConnectors) {
        const existing = next[c.id] ?? emptyChannelDraft(c.id, props.productName, props.productDescription ?? "", variantIds);
        next[c.id] = { ...existing, selected };
      }
      return next;
    });
  };

  const create = async () => {
    setBusy(true);
    setBanner("");
    setPerChannelError({});
    const ordered = listingConnectors.map((c) => drafts[c.id]).filter(Boolean);
    const { results, created, failed } = await createSelectedChannelDrafts(ordered, {
      basePrice,
      createOne: async (draft) => {
        const connector = listingConnectors.find((c) => c.id === draft.connectorId);
        const variantIdsForCreate = draft.variantMode === "all" ? undefined : draft.variantIds;
        const result = await createChannelListing({
          orgId: props.orgId,
          connectorId: draft.connectorId,
          productId: props.productId,
          channelTitle: draft.channelTitle || undefined,
          channelDescription: draft.channelDescription || undefined,
          priceStrategy: draft.priceStrategy,
          inventoryStrategy: draft.inventoryStrategy,
          mediaStrategy: draft.mediaStrategy,
          variantIds: variantIdsForCreate,
          listingType: props.variants.length > 1 ? "multi_variation" : "single",
          channelCategoryId: draft.categoryId || undefined,
          channelCategoryName: draft.categoryName || undefined,
          providerConfig: buildDraftProviderConfig(draft),
          provider: connector?.provider,
        });
        if (result.code === "already_exists") return result;
        if (result.success && result.listing_id && draft.mediaStrategy === "listing_override" && draft.primaryMediaAssetId) {
          const { error: mediaErr } = await supabase.rpc("set_listing_channel_primary_from_asset", {
            p_org_id: props.orgId,
            p_listing_id: result.listing_id,
            p_media_asset_id: draft.primaryMediaAssetId,
          });
          if (mediaErr) {
            return {
              success: false,
              listing_id: result.listing_id,
              error: `Draft was created but the chosen primary image could not be saved: ${mediaErr.message}`,
            };
          }
        }
        return result;
      },
    });
    const errors: Record<string, string> = {};
    for (const row of results) {
      if (!row.ok && row.error) errors[row.connectorId] = row.duplicate ? `${row.error} Existing draft was not duplicated.` : row.error;
    }
    setPerChannelError(errors);
    setBusy(false);
    if (created > 0 && failed === 0) {
      props.onCreated();
      props.onClose();
      return;
    }
    if (created > 0) {
      props.onCreated();
      setBanner(`Created ${created} draft listing${created === 1 ? "" : "s"}. ${failed} channel${failed === 1 ? "" : "s"} need attention.`);
      return;
    }
    if (failed > 0) setBanner("No new drafts were created. Fix the highlighted channels and try again.");
    if (selectedCount === 0) setBanner("Select at least one channel.");
  };

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title="Create listings"
      size="workspace"
      scrollBody
      footer={
        <>
          <button type="button" onClick={props.onClose} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg">
            Cancel
          </button>
          <button
            type="button"
            data-testid="listing-workspace-create"
            onClick={() => void create()}
            disabled={busy || selectedCount === 0}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg"
          >
            {busy ? "Creating drafts…" : `Create ${selectedCount} draft listing${selectedCount === 1 ? "" : "s"}`}
          </button>
        </>
      }
    >
      <div className="space-y-4" data-testid="listing-workspace">
        <div className="sticky top-0 z-10 -mx-1 px-1 py-2 bg-white/95 backdrop-blur-sm border-b border-slate-100">
          <p className="text-sm text-slate-600">
            Configure each authorized channel independently, then create draft listings in one step. Nothing is published until you use Publish on a listing.
          </p>
          <div className="flex gap-2 flex-wrap mt-2">
            <button type="button" onClick={() => toggleAll(true)} className="px-2 py-1 text-xs rounded-lg border border-slate-200">Select all</button>
            <button type="button" onClick={() => toggleAll(false)} className="px-2 py-1 text-xs rounded-lg border border-slate-200">Select none</button>
          </div>
        </div>
        {banner && <div className="px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-800">{banner}</div>}
        {listingConnectors.length === 0 && (
          <p className="text-sm text-slate-500">Every connected channel already has a listing for this product.</p>
        )}
        <div className={workspaceColumnClass(listingConnectors.length)}>
          {listingConnectors.map((c) => {
            const draft = drafts[c.id];
            if (!draft) return null;
            const ready = connectorReadyForDrafts(c.authStatus);
            const issues = draft.selected ? validateChannelDraft(draft, { basePrice }) : [];
            const price = calculatedDraftPrice(draft, basePrice);
            const label = connectorDisplayName(c);
            return (
              <section
                key={c.id}
                data-testid={`channel-panel-${c.id}`}
                className={`rounded-xl border p-4 space-y-3 min-w-0 ${draft.selected ? "border-emerald-200 bg-white" : "border-slate-200 bg-slate-50"} ${perChannelError[c.id] ? "ring-1 ring-red-300" : ""}`}
              >
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={draft.selected}
                    disabled={!ready}
                    onChange={(e) => patch(c.id, { selected: e.target.checked })}
                    data-testid={`channel-select-${c.id}`}
                  />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{label}</p>
                    <p className="text-xs text-slate-500">{connectorAuthLabel(c.authStatus)}</p>
                    {!ready && <p className="text-xs text-amber-700 mt-1">Not authorized yet. Connect it in Integrations to include it here.</p>}
                  </div>
                </label>
                {perChannelError[c.id] && (
                  <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700" data-testid={`channel-error-${c.id}`}>
                    {label}: {perChannelError[c.id]}
                  </div>
                )}
                {draft.selected && issues.length > 0 && (
                  <ul className="text-xs text-red-700 space-y-0.5">
                    {issues.map((i) => (
                      <li key={i.field}>{i.message}</li>
                    ))}
                  </ul>
                )}
                {draft.selected && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Channel title</label>
                      <input value={draft.channelTitle} onChange={(e) => patch(c.id, { channelTitle: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Channel description</label>
                      <textarea value={draft.channelDescription} onChange={(e) => patch(c.id, { channelDescription: e.target.value })} className={inputCls} rows={3} />
                    </div>
                    <ChannelCategoryPicker
                      orgId={props.orgId}
                      connectorId={c.id}
                      provider={c.provider}
                      selectedId={draft.categoryId}
                      selectedName={draft.categoryName}
                      confirmed={draft.categoryConfirmed}
                      hints={hints}
                      onSelect={(id, name) => patch(c.id, { categoryId: id, categoryName: name, categoryConfirmed: true })}
                      onClear={() => patch(c.id, { categoryId: "", categoryName: "", categoryConfirmed: false })}
                    />
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Pricing rule</label>
                      <select
                        value={draft.priceStrategy}
                        onChange={(e) => patch(c.id, { priceStrategy: e.target.value as PriceStrategy, priceValue: e.target.value === "inherit" ? "" : draft.priceValue })}
                        className={inputCls}
                      >
                        <option value="inherit">Same as base price</option>
                        <option value="percentage_markup">Percentage (+% / −%)</option>
                        <option value="fixed_markup">Fixed amount (+$ / −$)</option>
                        <option value="fixed_override">Explicit fixed price</option>
                      </select>
                    </div>
                    {draft.priceStrategy !== "inherit" && (
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {draft.priceStrategy === "percentage_markup"
                            ? "Adjustment % (e.g. 20 or -20)"
                            : draft.priceStrategy === "fixed_markup"
                              ? "Adjustment $ (e.g. 5 or -5)"
                              : "Fixed selling price"}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={draft.priceValue}
                          onChange={(e) => patch(c.id, { priceValue: e.target.value })}
                          className={inputCls}
                          placeholder={draft.priceStrategy === "percentage_markup" ? "20" : "5.00"}
                        />
                      </div>
                    )}
                    <p className="text-sm text-slate-600">
                      Calculated selling price:{" "}
                      <span className="font-semibold text-emerald-700" data-testid={`channel-price-${c.id}`}>{money(price)}</span>
                      <span className="text-xs text-slate-400"> (base {money(basePrice)})</span>
                    </p>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Inventory strategy</label>
                      <select value={draft.inventoryStrategy} onChange={(e) => patch(c.id, { inventoryStrategy: e.target.value as InventoryStrategy })} className={inputCls}>
                        <option value="all_available">All available</option>
                        <option value="fixed_cap">Fixed cap</option>
                        <option value="percentage_allocation">Percentage allocation</option>
                      </select>
                    </div>
                    {draft.inventoryStrategy === "fixed_cap" && (
                      <input type="number" min="0" value={draft.inventoryCap} onChange={(e) => patch(c.id, { inventoryCap: e.target.value })} className={inputCls} placeholder="Cap quantity" />
                    )}
                    {draft.inventoryStrategy === "percentage_allocation" && (
                      <input type="number" min="1" max="100" value={draft.inventoryPercent} onChange={(e) => patch(c.id, { inventoryPercent: e.target.value })} className={inputCls} placeholder="% of available" />
                    )}
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Media / primary image</label>
                      <select value={draft.mediaStrategy} onChange={(e) => patch(c.id, { mediaStrategy: e.target.value as MediaStrategy })} className={inputCls}>
                        <option value="inherit">Inherit product gallery</option>
                        <option value="listing_override">Choose primary image</option>
                      </select>
                      {draft.mediaStrategy === "listing_override" && (
                        <div className="mt-2 space-y-1">
                          {props.images.length === 0 && <p className="text-xs text-slate-500">No product images yet.</p>}
                          {props.images.map((img) => (
                            <label key={img.mediaAssetId} className="flex items-center gap-2 text-xs text-slate-700">
                              <input
                                type="radio"
                                name={`primary-${c.id}`}
                                checked={draft.primaryMediaAssetId === img.mediaAssetId}
                                onChange={() => patch(c.id, { primaryMediaAssetId: img.mediaAssetId })}
                              />
                              {img.filename || img.mediaAssetId}{img.isPrimary ? " (product primary)" : ""}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Variants</label>
                      <select value={draft.variantMode} onChange={(e) => patch(c.id, { variantMode: e.target.value as "all" | "selected" })} className={inputCls}>
                        <option value="all">All active variants ({props.variants.length})</option>
                        <option value="selected">Selected variants</option>
                      </select>
                      {draft.variantMode === "selected" && (
                        <div className="mt-2 max-h-32 overflow-auto space-y-1">
                          {props.variants.map((v) => {
                            const checked = draft.variantIds.includes(v.id);
                            const labelText = v.option_values ? Object.values(v.option_values).join(" / ") : (v.sku ?? v.id);
                            return (
                              <label key={v.id} className="flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => {
                                    const next = checked ? draft.variantIds.filter((id) => id !== v.id) : [...draft.variantIds, v.id];
                                    patch(c.id, { variantIds: next });
                                  }}
                                />
                                {labelText}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
        {listingConnectors.length > 0 && (
          <p className="text-xs text-slate-400">Drafts stay local. Pricing, media and category settings are not sent to marketplaces until Publish.</p>
        )}
      </div>
    </Modal>
  );
}
